import { useMutation } from '@tanstack/react-query';
import { skip } from '@signalvote/data';
import type {
  AccountSelectionResult,
  BankConnectionView,
  BankConnectionLinkResult,
  BankLinkTokenResult,
  ExchangePublicTokenRequest,
  SelectAccountsRequest,
  SyncNowResult,
} from '@signalvote/shared';
import { apiJson, apiVoid, type ApiErrorBody } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { useRefreshFinanceAfter } from './useFinanceSettings';

// ============================================================================
// Plaid bank-pull — the guided link flow (ADR-070, #562) + the connection-health
// surface (#563), against `api/finance/bank-connections` (all `Finance:Manage`).
//
// Link flow (mutations, treasurer actions):
//   1. create a Link token   → open Plaid Link in the browser
//   2. exchange public_token  → persist the connection + list selectable accounts
//   3. select accounts        → track the chosen accounts + run the first pull
//
// Connection health (#563):
//   - list connections        → the settings surface reads status per connection
//   - reconnect-token          → mint an update-mode Link token for the SAME Item
//   - reconnect                → re-activate on Plaid Link update success (no re-select)
//   - disconnect               → revoke at Plaid + flip to Disconnected
//
// Each write saves through the request, so its write set refreshes the defined reads. Selecting
// accounts and sync now pull rows into the triage inbox, so they also refresh the finance reads
// that are not defined reads yet. A pull the daily poll or a Plaid webhook runs sends no write set
// to the page; it shows on the next fetch.
// ============================================================================

const BASE = '/api/finance/bank-connections';

/** Reads the `{ error }` message the controller returns, falling back to a generic message. */
function readError(fallback: string) {
  return (body: ApiErrorBody) => new Error(body.error || fallback);
}

/**
 * Lists the campaign's linked bank connections + their health (#563) for the
 * Banks & processors connection-status surface.
 *
 * The endpoint is Finance:Manage — pass `{ enabled: false }` to skip the fetch for
 * a viewer without that tier (e.g. the ledger's exclusions note on a view-only
 * close hub) rather than firing a doomed 403.
 */
const bankConnectionsRead = defineQuery({
  key: () => ['finance', 'bank-connections'],
  reads: 'GET /api/finance/bank-connections',
  fetch: (_: undefined, { signal, campaignId }): Promise<BankConnectionView[]> =>
    apiJson<BankConnectionView[]>(BASE, {
      signal,
      headers: campaignHeaders(campaignId),
      error: readError('Failed to load bank connections'),
    }),
});

export function useBankConnections(options?: { enabled?: boolean }) {
  return bankConnectionsRead(options?.enabled === false ? skip : undefined);
}

/**
 * Step 1 of the reconnect handshake (#563): mints a Plaid Link **update-mode**
 * token for the same Item so the treasurer can re-authenticate a `LoginRequired`
 * connection. The returned `linkToken` opens Plaid Link in update mode — no
 * account re-selection follows.
 */
export function useReconnectBankToken() {
  return useMutation({
    mutationFn: (connectionId: string): Promise<BankLinkTokenResult> =>
      apiJson<BankLinkTokenResult>(`${BASE}/${connectionId}/reconnect-token`, {
        method: 'POST',
        error: readError('Failed to start reconnect'),
      }),
  });
}

/**
 * Step 2 of the reconnect handshake (#563): re-activates the connection after
 * Plaid Link's update-mode success, flipping `LoginRequired → Active` with the
 * cursor + tracked accounts preserved. It pulls nothing itself: the syncs it resumes run in
 * the background, after the write set.
 */
export const useReconnectBankConnection = defineWrite({
  send: (connectionId: string): Promise<void> =>
    apiVoid(`${BASE}/${connectionId}/reconnect`, {
      method: 'POST',
      error: readError('Failed to reconnect the bank'),
    }),
});

/**
 * Disconnects a connection (#563): revokes the token at Plaid and flips it to
 * `Disconnected` so no further pulls occur. Re-linking later creates a fresh
 * Plaid Item — this is not reversible.
 */
export const useDisconnectBankConnection = defineWrite({
  send: (connectionId: string): Promise<void> =>
    apiVoid(`${BASE}/${connectionId}/disconnect`, {
      method: 'POST',
      error: readError('Failed to disconnect the bank'),
    }),
});

/**
 * On-demand "sync now" for an existing connection (`POST {id}/sync`): pulls the
 * latest transactions your bank has already shared with Plaid through the same
 * ingest path as the daily poll — the free `/transactions/sync` cursor read, NOT
 * a billable live refresh. A 409 ("already in progress") surfaces its message to the caller.
 */
const useSyncBankConnectionWrite = defineWrite({
  send: (connectionId: string): Promise<SyncNowResult> =>
    apiJson<SyncNowResult>(`${BASE}/${connectionId}/sync`, {
      method: 'POST',
      error: readError('Failed to sync the bank'),
    }),
});

export function useSyncBankConnection() {
  return useRefreshFinanceAfter(useSyncBankConnectionWrite());
}

/**
 * Mints a first-time Plaid Link token (sized to reach the campaign's in-period
 * boundary server-side). The returned `linkToken` opens Plaid Link.
 */
export function useCreateBankLinkToken() {
  return useMutation({
    mutationFn: (): Promise<BankLinkTokenResult> =>
      apiJson<BankLinkTokenResult>(`${BASE}/link-token`, {
        method: 'POST',
        error: readError('Failed to start bank linking'),
      }),
  });
}

/**
 * Trades Link's `public_token` for a persisted connection and the Item's
 * selectable (checking / savings / credit) accounts for the treasurer to pick.
 * No account is tracked and nothing is pulled yet — that happens at selection.
 */
export function useExchangePublicToken() {
  return useMutation({
    mutationFn: (publicToken: string): Promise<BankConnectionLinkResult> =>
      apiJson<BankConnectionLinkResult>(`${BASE}/exchange`, {
        method: 'POST',
        json: { publicToken } satisfies ExchangePublicTokenRequest,
        error: readError('Failed to link the bank'),
      }),
  });
}

export interface SelectBankAccountsInput {
  connectionId: string;
  plaidAccountIds: string[];
}

/**
 * Persists the treasurer's chosen accounts as tracked accounts and runs the
 * first pull (the same ingest path as an ongoing sync).
 */
const useSelectBankAccountsWrite = defineWrite({
  send: ({ connectionId, plaidAccountIds }: SelectBankAccountsInput): Promise<AccountSelectionResult> =>
    apiJson<AccountSelectionResult>(`${BASE}/${connectionId}/accounts`, {
      method: 'POST',
      json: { plaidAccountIds } satisfies SelectAccountsRequest,
      error: readError('Failed to save account selection'),
    }),
});

export function useSelectBankAccounts() {
  return useRefreshFinanceAfter(useSelectBankAccountsWrite());
}
